import { WalletConnectMoleculeReatom as WalletConnectMolecule } from "@/app/wallet-connect/store.reatom"
import {
  type UnionAddress,
  getBlockchainFromAddress,
  getRawAddress,
} from "@anaconda/core/blockchain/address"
import { Blockchain } from "@anaconda/core/blockchain/types"
import { useMolecule } from "bunshi/react"
import { useMemo } from "react"

interface Props {
  owner?: UnionAddress
  collection?: { blockchain: Blockchain }
}

export function isUserOwnedNft(
  owner: UnionAddress | undefined,
  options: {
    blockchain: Blockchain
    evmAddress?: string
    solanaAddress?: string
  },
): boolean {
  if (!owner) {
    return false
  }

  const connectedAddress =
    options.blockchain === Blockchain.SOLANA
      ? options.solanaAddress
      : options.evmAddress

  if (!connectedAddress) {
    return false
  }

  const ownerAddress = getRawAddress(owner)

  if (options.blockchain === Blockchain.SOLANA) {
    return ownerAddress === connectedAddress
  }

  return ownerAddress.toLowerCase() === connectedAddress.toLowerCase()
}

export const useUserOwnedNft = ({ owner, collection }: Props) => {
  const { evmAddress, solanaAddress } = useMolecule(WalletConnectMolecule)
  const evmWalletAddress = evmAddress()
  const solanaWalletAddress = solanaAddress()

  return useMemo(() => {
    const blockchain =
      collection?.blockchain ??
      (owner ? getBlockchainFromAddress(owner) : undefined)

    if (!blockchain) {
      return { userConnected: false } as const
    }

    const connectedForBlockchain =
      blockchain === Blockchain.SOLANA ? solanaWalletAddress : evmWalletAddress

    if (!connectedForBlockchain) {
      return { userConnected: false } as const
    }

    return {
      userConnected: true,
      userOwnedNft: isUserOwnedNft(owner, {
        blockchain,
        evmAddress: evmWalletAddress,
        solanaAddress: solanaWalletAddress,
      }),
    } as const
  }, [collection?.blockchain, evmWalletAddress, owner, solanaWalletAddress])
}
